import type { UserProfile } from '../types.js';

/**
 * Synthetic personas. Entirely fabricated - no real people, accounts or market
 * data anywhere in this repository. They exist so a first-time user (or a demo
 * judge) can see a fully populated platform in one click instead of typing a
 * balance sheet, and so the three main financial life-stages are all reachable.
 */

const YEAR = new Date().getUTCFullYear();

export interface Persona {
  id: string;
  label: string;
  tagline: string;
  /** One-line description of the planning problem this persona illustrates. */
  challenge: string;
  profile: UserProfile;
}

function iso(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString();
}

export const PERSONAS: Persona[] = [
  {
    id: 'aarav',
    label: 'Aarav, 26 - early career',
    tagline: 'Good income, no buffer, expensive card debt',
    challenge:
      'Earning well but carrying a 42% credit-card balance with barely a month of emergency cover. The engine should refuse to talk about investing until that is fixed.',
    profile: {
      id: 'persona-aarav',
      displayName: 'Aarav Sharma',
      age: 26,
      retirementAge: 60,
      dependents: 0,
      incomeStability: 'stable',
      currency: 'INR',
      liquidSavings: 85000,
      lifeInsuranceCover: 0,
      healthInsuranceCover: 500000,
      cashflow: {
        monthlyNetIncome: 95000,
        otherMonthlyIncome: 0,
        annualIncomeGrowthPct: 0.12,
        monthlyExpenses: {
          Rent: 24000,
          Food: 12000,
          Transport: 5000,
          Utilities: 3500,
          Subscriptions: 2500,
          Lifestyle: 14000,
          Other: 4000,
        },
      },
      holdings: [
        {
          id: 'h1',
          symbol: 'NIFTYBEES',
          name: 'Nifty 50 Index Fund',
          assetClass: 'equity_domestic',
          units: 420,
          price: 285,
          costBasis: 105000,
          expenseRatioPct: 0.002,
          instrumentKind: 'fund',
          account: 'Demat',
        },
        {
          id: 'h2',
          symbol: 'EMPSTOCK',
          name: 'Employer Stock (RSU)',
          assetClass: 'equity_domestic',
          units: 60,
          price: 1850,
          costBasis: 84000,
          instrumentKind: 'security',
          account: 'RSU',
        },
        {
          id: 'h3',
          symbol: 'LIQUIDF',
          name: 'Liquid Fund',
          assetClass: 'cash',
          units: 1200,
          price: 32,
          costBasis: 37000,
          expenseRatioPct: 0.0025,
          instrumentKind: 'deposit',
          account: 'MF',
        },
      ],
      liabilities: [
        {
          id: 'l1',
          name: 'Credit Card',
          kind: 'credit_card',
          outstanding: 148000,
          interestRatePct: 0.42,
          emi: 9000,
        },
        {
          id: 'l2',
          name: 'Education Loan',
          kind: 'education_loan',
          outstanding: 320000,
          interestRatePct: 0.095,
          emi: 8200,
          remainingMonths: 46,
        },
      ],
      goals: [
        {
          id: 'g1',
          name: 'Emergency Fund',
          kind: 'emergency',
          targetAmountToday: 390000,
          targetYear: YEAR + 2,
          currentSaved: 85000,
          monthlyContribution: 5000,
          contributionStepUpPct: 0,
          priority: 'must_have',
        },
        {
          id: 'g2',
          name: 'Retirement',
          kind: 'retirement',
          targetAmountToday: 30000000,
          targetYear: YEAR + 34,
          currentSaved: 230000,
          monthlyContribution: 6000,
          contributionStepUpPct: 0,
          priority: 'must_have',
        },
        {
          id: 'g3',
          name: 'Japan Trip',
          kind: 'travel',
          targetAmountToday: 250000,
          targetYear: YEAR + 3,
          currentSaved: 20000,
          monthlyContribution: 3000,
          contributionStepUpPct: 0,
          priority: 'aspirational',
        },
      ],
      riskAnswers: { drawdown: 3, experience: 1, tradeoff: 2, horizon: 3, buffer: 1, flexibility: 2 },
      createdAt: iso(-120),
      updatedAt: iso(),
      isSynthetic: true,
    },
  },
  {
    id: 'meera',
    label: 'Meera, 38 - mid career, two kids',
    tagline: 'Competing goals, home loan, under-insured',
    challenge:
      'Three must-have goals bidding for the same surplus, with education costs inflating faster than everything else and only 4x income of life cover against two dependents.',
    profile: {
      id: 'persona-meera',
      displayName: 'Meera Iyer',
      age: 38,
      retirementAge: 58,
      dependents: 2,
      incomeStability: 'stable',
      currency: 'INR',
      liquidSavings: 620000,
      lifeInsuranceCover: 8000000,
      healthInsuranceCover: 1000000,
      cashflow: {
        monthlyNetIncome: 210000,
        otherMonthlyIncome: 18000,
        annualIncomeGrowthPct: 0.08,
        monthlyExpenses: {
          'Home EMI support': 12000,
          Groceries: 28000,
          'School fees': 32000,
          Transport: 9000,
          Utilities: 7500,
          Help: 12000,
          Healthcare: 6000,
          Lifestyle: 22000,
          Other: 8000,
        },
      },
      holdings: [
        {
          id: 'h1',
          symbol: 'FLEXICAP',
          name: 'Flexi Cap Fund',
          assetClass: 'equity_domestic',
          units: 14500,
          price: 96,
          costBasis: 980000,
          expenseRatioPct: 0.0175,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h2',
          symbol: 'MIDCAP',
          name: 'Mid Cap Fund',
          assetClass: 'equity_domestic',
          units: 6200,
          price: 118,
          costBasis: 540000,
          expenseRatioPct: 0.0192,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h3',
          symbol: 'USINDEX',
          name: 'US Index Fund of Funds',
          assetClass: 'equity_international',
          units: 3100,
          price: 61,
          costBasis: 155000,
          expenseRatioPct: 0.0055,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h4',
          symbol: 'CORPBOND',
          name: 'Corporate Bond Fund',
          assetClass: 'debt',
          units: 9800,
          price: 42,
          costBasis: 380000,
          expenseRatioPct: 0.006,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h5',
          symbol: 'PPF',
          name: 'Provident Fund',
          assetClass: 'debt',
          units: 1,
          price: 1850000,
          costBasis: 1400000,
          instrumentKind: 'deposit',
          account: 'Retirement',
        },
        {
          id: 'h6',
          symbol: 'GOLDBEES',
          name: 'Gold ETF',
          assetClass: 'gold',
          units: 2400,
          price: 68,
          costBasis: 128000,
          expenseRatioPct: 0.005,
          instrumentKind: 'fund',
          account: 'Demat',
        },
      ],
      liabilities: [
        {
          id: 'l1',
          name: 'Home Loan',
          kind: 'home_loan',
          outstanding: 4800000,
          interestRatePct: 0.0875,
          emi: 46000,
          remainingMonths: 172,
        },
        {
          id: 'l2',
          name: 'Car Loan',
          kind: 'car_loan',
          outstanding: 420000,
          interestRatePct: 0.105,
          emi: 14500,
          remainingMonths: 32,
        },
      ],
      goals: [
        {
          id: 'g1',
          name: "Children's Education",
          kind: 'education',
          targetAmountToday: 6000000,
          targetYear: YEAR + 10,
          currentSaved: 1150000,
          monthlyContribution: 22000,
          contributionStepUpPct: 0.05,
          priority: 'must_have',
          inflationOverridePct: 0.1,
          notes: 'Education inflation runs well above headline CPI.',
        },
        {
          id: 'g2',
          name: 'Retirement',
          kind: 'retirement',
          targetAmountToday: 60000000,
          targetYear: YEAR + 20,
          currentSaved: 2900000,
          monthlyContribution: 28000,
          contributionStepUpPct: 0.05,
          priority: 'must_have',
        },
        {
          id: 'g3',
          name: 'Home Renovation',
          kind: 'home',
          targetAmountToday: 1500000,
          targetYear: YEAR + 4,
          currentSaved: 260000,
          monthlyContribution: 12000,
          contributionStepUpPct: 0,
          priority: 'important',
        },
      ],
      riskAnswers: { drawdown: 2, experience: 2, tradeoff: 2, horizon: 3, buffer: 2, flexibility: 1 },
      createdAt: iso(-400),
      updatedAt: iso(),
      isSynthetic: true,
    },
  },
  {
    id: 'rohan',
    label: 'Rohan, 52 - pre-retirement',
    tagline: 'Eight years out, portfolio too aggressive, one big position',
    challenge:
      'Close enough to retirement that sequencing risk dominates, but holding 78% equity with a quarter of it in one stock. The glide path should pull risk down hard.',
    profile: {
      id: 'persona-rohan',
      displayName: 'Rohan Desai',
      age: 52,
      retirementAge: 60,
      dependents: 1,
      incomeStability: 'variable',
      currency: 'INR',
      liquidSavings: 1450000,
      lifeInsuranceCover: 15000000,
      healthInsuranceCover: 2000000,
      cashflow: {
        monthlyNetIncome: 340000,
        otherMonthlyIncome: 45000,
        annualIncomeGrowthPct: 0.05,
        monthlyExpenses: {
          Groceries: 34000,
          'College fees': 45000,
          Transport: 14000,
          Utilities: 11000,
          Healthcare: 14000,
          Travel: 18000,
          Lifestyle: 26000,
          Other: 12000,
        },
      },
      holdings: [
        {
          id: 'h1',
          symbol: 'LARGECAP',
          name: 'Large Cap Fund',
          assetClass: 'equity_domestic',
          units: 32000,
          price: 148,
          costBasis: 3100000,
          expenseRatioPct: 0.0145,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h2',
          symbol: 'LEGACYSTK',
          name: 'Legacy Single Stock',
          assetClass: 'equity_domestic',
          units: 4200,
          price: 1420,
          costBasis: 1250000,
          instrumentKind: 'security',
          account: 'Demat',
        },
        {
          id: 'h3',
          symbol: 'SMALLCAP',
          name: 'Small Cap Fund',
          assetClass: 'equity_domestic',
          units: 9800,
          price: 172,
          costBasis: 1180000,
          expenseRatioPct: 0.0198,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h4',
          symbol: 'USINDEX',
          name: 'US Index Fund of Funds',
          assetClass: 'equity_international',
          units: 14200,
          price: 61,
          costBasis: 690000,
          expenseRatioPct: 0.0055,
          instrumentKind: 'fund',
          account: 'MF',
        },
        {
          id: 'h5',
          symbol: 'EPF',
          name: 'Employee Provident Fund',
          assetClass: 'debt',
          units: 1,
          price: 5200000,
          costBasis: 4100000,
          instrumentKind: 'deposit',
          account: 'Retirement',
        },
        {
          id: 'h6',
          symbol: 'REITIND',
          name: 'Office REIT',
          assetClass: 'reit',
          units: 1800,
          price: 340,
          costBasis: 590000,
          expenseRatioPct: 0.004,
          instrumentKind: 'fund',
          account: 'Demat',
        },
      ],
      liabilities: [
        {
          id: 'l1',
          name: 'Home Loan (tail end)',
          kind: 'home_loan',
          outstanding: 1200000,
          interestRatePct: 0.085,
          emi: 38000,
          remainingMonths: 36,
        },
      ],
      goals: [
        {
          id: 'g1',
          name: 'Retirement',
          kind: 'retirement',
          targetAmountToday: 90000000,
          targetYear: YEAR + 8,
          currentSaved: 12400000,
          monthlyContribution: 85000,
          contributionStepUpPct: 0.05,
          priority: 'must_have',
        },
        {
          id: 'g2',
          name: "Daughter's Post-Graduation",
          kind: 'education',
          targetAmountToday: 4500000,
          targetYear: YEAR + 3,
          currentSaved: 1900000,
          monthlyContribution: 45000,
          contributionStepUpPct: 0,
          priority: 'must_have',
          inflationOverridePct: 0.09,
        },
        {
          id: 'g3',
          name: 'Second Home',
          kind: 'home',
          targetAmountToday: 12000000,
          targetYear: YEAR + 9,
          currentSaved: 800000,
          monthlyContribution: 15000,
          contributionStepUpPct: 0,
          priority: 'aspirational',
        },
      ],
      riskAnswers: { drawdown: 3, experience: 3, tradeoff: 3, horizon: 2, buffer: 3, flexibility: 3 },
      createdAt: iso(-800),
      updatedAt: iso(),
      isSynthetic: true,
    },
  },
];

export function personaById(id: string): Persona | undefined {
  return PERSONAS.find((p) => p.id === id || p.profile.id === id);
}

/**
 * Blank profile for a user who wants to enter their own numbers. Expense rows
 * are pre-seeded with zero so the onboarding wizard has a shape to fill.
 */
export function emptyProfile(id: string, displayName: string): UserProfile {
  return {
    id,
    displayName,
    age: 30,
    retirementAge: 60,
    dependents: 0,
    incomeStability: 'stable',
    currency: 'INR',
    liquidSavings: 0,
    lifeInsuranceCover: 0,
    healthInsuranceCover: 0,
    cashflow: {
      monthlyNetIncome: 0,
      otherMonthlyIncome: 0,
      annualIncomeGrowthPct: 0.08,
      monthlyExpenses: { Housing: 0, Food: 0, Transport: 0, Utilities: 0, Lifestyle: 0, Other: 0 },
    },
    holdings: [],
    liabilities: [],
    goals: [],
    riskAnswers: {},
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isSynthetic: true,
  };
}
